import React, { useState, useEffect } from 'react';
import {
    Container,
    Paper,
    Typography,
    Box,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    IconButton,
    TextField,
    InputAdornment,
    Chip,
    Stack,
    Card,
    CardContent
} from '@mui/material';
import {
    Print as PrintIcon,
    Preview as PreviewIcon,
    Search as SearchIcon,
    Settings as SettingsIcon,
    Refresh as RefreshIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const PassbookFirstPagePrint = () => {
    const { user } = useAuth();
    
    // State for settings
    const [savedSettings, setSavedSettings] = useState([]);
    const [selectedSetting, setSelectedSetting] = useState('');
    const [fieldSettings, setFieldSettings] = useState({});
    const [fontSize, setFontSize] = useState(14);
    
    // State for customers and pagination
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    
    // State for UI
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // All available customer fields from the database - mapped to actual API response property names
    const customerFields = [
        { key: 'consumerNumber', label: 'Consumer Number', field: 'consumerNumber' },
        { key: 'consumer_Name', label: 'Consumer Name', field: 'consumer_Name' },
        { key: 'address1', label: 'Address Line 1', field: 'address1' },
        { key: 'address2', label: 'Address Line 2', field: 'address2' },
        { key: 'cardSerialNumber', label: 'Card Serial Number', field: 'cardSerialNumber' },
        { key: 'sV_Number', label: 'SV Number', field: 'sV_Number' },
        { key: 'sV_Date', label: 'SV Date', field: 'sV_Date' },
        { key: 'sV_Number_DBC', label: 'SV Number DBC', field: 'sV_Number_DBC' },
        { key: 'sV_Date_DBC', label: 'SV Date DBC', field: 'sV_Date_DBC' },
        { key: 'no_Of_Cylinders', label: 'Number of Cylinders', field: 'no_Of_Cylinders' },
        { key: 'deposit_Rs', label: 'Deposit (Rs)', field: 'deposit_Rs' },
        { key: 'no_of_Regulrators', label: 'Number of Regulators', field: 'no_of_Regulrators' },
        { key: 'no_of_Regulrators_Deposit', label: 'Regulator Deposit', field: 'no_of_Regulrators_Deposit' },
        { key: 'distributors_Code', label: 'Distributor Code', field: 'distributors_Code' },
        { key: 'distributors_Name', label: 'Distributor Name', field: 'distributors_Name' },
        { key: 'distributors_Address1', label: 'Distributor Address 1', field: 'distributors_Address1' },
        { key: 'distributors_Address2', label: 'Distributor Address 2', field: 'distributors_Address2' },
        { key: 'distributors_Phone', label: 'Distributor Phone', field: 'distributors_Phone' },
        { key: 'distributors_State', label: 'Distributor State', field: 'distributors_State' },
        { key: 'distributors_City', label: 'Distributor City', field: 'distributors_City' },
        { key: 'distributors_PIN', label: 'Distributor PIN', field: 'distributors_PIN' },
        { key: 'issuing_Company', label: 'Issuing Company', field: 'issuing_Company' }
    ];

    useEffect(() => {
        loadSavedSettings();
        loadCustomers();
    }, []);

    useEffect(() => {
        loadCustomers();
    }, [page, rowsPerPage, searchTerm]);

    const loadSavedSettings = async () => {
        try {
            const response = await api.get('/api/passbooksettings/names');
            setSavedSettings(response.data);
            
            // Auto-select the first setting if available
            if (response.data.length > 0) {
                const defaultSetting = response.data[0];
                setSelectedSetting(defaultSetting);
                loadSettings(defaultSetting);
            }
        } catch (error) {
            console.error('Error loading saved settings:', error);
            setError('Failed to load settings');
        }
    };

    const loadSettings = async (settingName) => {
        if (!settingName) return;
        
        try {
            const response = await api.get(`/api/passbooksettings/${settingName}`);
            const settings = response.data;
            
            const newFieldSettings = {};
            let loadedFontSize = 14;
            
            settings.forEach(setting => {
                newFieldSettings[setting.fieldName] = {
                    top: setting.topPosition,
                    left: setting.leftPosition,
                    visible: setting.isVisible,
                    order: setting.displayOrder || 0
                };
                
                if (setting.fontSize) {
                    loadedFontSize = setting.fontSize;
                }
            });
            
            setFieldSettings(newFieldSettings);
            setFontSize(loadedFontSize);
        } catch (error) {
            console.error('Error loading settings:', error);
            setError('Failed to load settings: ' + error.message);
        }
    };

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const params = {
                pageNumber: page + 1, // Convert 0-based to 1-based pagination
                pageSize: rowsPerPage
            };
            
            if (searchTerm) {
                params.search = searchTerm;
            }
            
            const response = await api.get('/api/customers', { params });
            
            console.log('Customer API response:', response.data);
            // Handle PaginatedCustomerResponseDto structure
            if (response.data && response.data.data && Array.isArray(response.data.data)) {
                setCustomers(response.data.data);
                setTotalCount(response.data.pagination?.totalRecords || 0);
            } else if (response.data && Array.isArray(response.data)) {
                // Fallback for direct array response (backwards compatibility)
                setCustomers(response.data);
                setTotalCount(response.data.length);
            } else {
                setError('Invalid response format from server');
                setCustomers([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
            setError('Failed to load customers: ' + (error.response?.data?.message || error.message));
            setCustomers([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0); // Reset to first page when searching
    };

    const handleSettingChange = (event) => {
        const settingName = event.target.value;
        setSelectedSetting(settingName);
        loadSettings(settingName);
    };

    const formatFieldValue = (customer, fieldKey) => {
        if (!customer) return 'N/A';
        
        switch (fieldKey) {
            case 'sV_Date':
            case 'sV_Date_DBC':
                return customer[fieldKey] ? new Date(customer[fieldKey]).toLocaleDateString() : 'N/A';
            case 'createdDate':
                return new Date(customer.createdDate).toLocaleDateString();
            case 'isActive':
                return customer.isActive ? 'Active' : 'Inactive';
            case 'id':
                return customer.id.toString();
            case 'tenantId':
                return customer.tenantId.toString();
            case 'no_Of_Cylinders':
            case 'no_of_Regulrators':
                return customer[fieldKey]?.toString() || 'N/A';
            case 'deposit_Rs':
            case 'no_of_Regulrators_Deposit':
                return customer[fieldKey] ? `Rs. ${customer[fieldKey]}` : 'N/A';
            default:
                return customer[fieldKey] || 'N/A';
        }
    };

    const generateFirstPageHTML = (customer) => {
        const visibleFields = customerFields
            .filter(field => fieldSettings[field.key]?.visible)
            .sort((a, b) => (fieldSettings[a.key]?.order || 0) - (fieldSettings[b.key]?.order || 0));
        
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Gas Passbook - First Page</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        margin: 0;
                        padding: 20px;
                        font-size: 14px;
                        position: relative;
                        width: 9in;
                        height: 14in;
                        min-height: 14in;
                    }
                    .field {
                        position: absolute;
                        font-weight: bold;
                        font-size: ${fontSize}px;
                    }
                    @page {
                        size: 9in 14in;
                        margin: 0;
                    }
                    @media print {
                        html, body { 
                            width: 9in !important;
                            height: 14in !important;
                            margin: 0 !important; 
                            padding: 0 !important;
                            background: none !important;
                            border: none !important;
                            overflow: hidden !important;
                        }
                        .field { 
                            border: none !important; 
                            background: none !important;
                            padding: 0 !important;
                        }
                        * {
                            -webkit-print-color-adjust: exact !important;
                            color-adjust: exact !important;
                        }
                    }
                </style>
            </head>
            <body>
                ${visibleFields.map(field => `
                    <div class="field" style="top: ${fieldSettings[field.key].top + 30}px; left: ${fieldSettings[field.key].left}px;">
                        ${formatFieldValue(customer, field.key)}
                    </div>
                `).join('')}
            </body>
            </html>
        `;
    };

    const handlePreview = (customer) => {
        if (!selectedSetting) {
            setError('Please select a setting template first');
            return;
        }
        
        const previewContent = generateFirstPageHTML(customer);
        const previewWindow = window.open('', '_blank', 'width=900,height=1400');
        previewWindow.document.write(previewContent);
        previewWindow.document.close();
    };

    const handlePrint = (customer) => {
        if (!selectedSetting) {
            setError('Please select a setting template first');
            return;
        }
        
        const printContent = generateFirstPageHTML(customer);
        const printWindow = window.open('', '_blank', 'width=900,height=1400');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();
        
        // Wait for content to load before printing
        setTimeout(() => {
            printWindow.print();
        }, 500);
    };

    const getVisibleFieldsCount = () => {
        return Object.values(fieldSettings).filter(field => field.visible).length;
    };

    return (
        <Container maxWidth="xl">
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    Passbook First Page Print
                </Typography>
                <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    Select customers and print their information on passbook first pages
                </Typography>
                
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}
                
                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}
            </Box>

            {/* Settings Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Print Settings
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
                        <Box sx={{ minWidth: 300 }}>
                            <FormControl fullWidth>
                                <InputLabel>Select Print Template</InputLabel>
                                <Select
                                    value={selectedSetting}
                                    label="Select Print Template"
                                    onChange={handleSettingChange}
                                >
                                    {savedSettings.map((name) => (
                                        <MenuItem key={name} value={name}>
                                            {name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {selectedSetting && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip 
                                    icon={<SettingsIcon />}
                                    label={`Template: ${selectedSetting}`}
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip 
                                    label={`${getVisibleFieldsCount()} fields configured`}
                                    color="success"
                                    size="small"
                                />
                                <Chip 
                                    label={`Font: ${fontSize}px`}
                                    color="info"
                                    size="small"
                                />
                            </Box>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            {/* Customer Search and Table */}
            <Paper sx={{ width: '100%', mb: 2 }}>
                <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                        <Typography variant="h6">
                            Customers
                        </Typography>
                        <Stack direction="row" spacing={2}>
                            <TextField
                                size="small"
                                placeholder="Search customers..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ minWidth: 250 }}
                            />
                            <Button
                                variant="outlined"
                                startIcon={<RefreshIcon />}
                                onClick={loadCustomers}
                                disabled={loading}
                            >
                                Refresh
                            </Button>
                        </Stack>
                    </Stack>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Consumer Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Customer Name</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Address</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Card Serial</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>SV Number</TableCell>
                                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Status</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e3f2fd' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        Loading customers...
                                    </TableCell>
                                </TableRow>
                            ) : customers.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                                        No customers found
                                    </TableCell>
                                </TableRow>
                            ) : (
                                customers.map((customer) => (
                                    <TableRow key={customer.id || customer.consumerNumber} hover>
                                        <TableCell>{customer.consumerNumber}</TableCell>
                                        <TableCell>{customer.consumer_Name}</TableCell>
                                        <TableCell>
                                            {customer.address1}
                                            {customer.address2 && `, ${customer.address2}`}
                                        </TableCell>
                                        <TableCell>{customer.cardSerialNumber || 'N/A'}</TableCell>
                                        <TableCell>{customer.sV_Number || 'N/A'}</TableCell>
                                        <TableCell>
                                            <Chip 
                                                label={customer.isActive ? 'Active' : 'Inactive'}
                                                color={customer.isActive ? 'success' : 'default'}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => handlePreview(customer)}
                                                    disabled={!selectedSetting}
                                                    title="Preview"
                                                >
                                                    <PreviewIcon />
                                                </IconButton>
                                                <IconButton
                                                    color="success"
                                                    onClick={() => handlePrint(customer)}
                                                    disabled={!selectedSetting}
                                                    title="Print"
                                                >
                                                    <PrintIcon />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Container>
    );
};

export default PassbookFirstPagePrint;